import React, { FC, KeyboardEventHandler } from 'react';

import { Box, Flex, FlexProps, LinkProps, Text } from '@rover/kibble/core';
import { LinkIcon } from '@rover/kibble/core/Link/Link';
import { useTheme } from '@rover/kibble/themes';

/*
 * We need this button to look identical to NavBarLink.
 * We don't currently have a Kibble component that can do this.
 * Building it from a Flex ended up being less complicated
 * than customizing a Kibble Button.
 *
 * This component hard-codes the "secondary" variant values
 *
 * This should be replaced with Kibble once we have a core component
 * that can do this.
 */

type CustomKeybindingProps = {
  id?: string;
  onKeyDown?: KeyboardEventHandler;
  onKeyPress?: KeyboardEventHandler;
  tabIndex?: number;
};

type LinkButtonIconProps = Pick<LinkProps, 'icon' | 'iconAlignment'>;

export type Props = FlexProps & CustomKeybindingProps & LinkButtonIconProps;

const LinkButtonIcon: FC<LinkButtonIconProps> = ({ icon, iconAlignment }) => (
  <Box
    as={LinkIcon}
    flexShrink="0"
    height="16px"
    width="16px"
    icon={icon}
    fill="currentColor"
    {...(iconAlignment === 'left' ? ({ mr: '2x' } as const) : ({ ml: '2x' } as const))}
  />
);

const LinkButton: FC<Props> = ({ children, icon, iconAlignment = 'left', ...other }) => {
  const theme = useTheme();

  return (
    <Flex
      as="button"
      role="button"
      width="100%"
      height="100%"
      background="navigation"
      border="none"
      alignItems="center"
      paddingX="0x"
      sx={{
        cursor: 'pointer',
        color: theme.textColor.navigation.toString(),
        ':hover': { color: theme.textColor.navigation_hover.toString() },
      }}
      {...other}
    >
      {icon && iconAlignment === 'left' && (
        <LinkButtonIcon icon={icon} iconAlignment={iconAlignment} />
      )}
      <Text size="100">{children}</Text>
      {icon && iconAlignment === 'right' && (
        <LinkButtonIcon icon={icon} iconAlignment={iconAlignment} />
      )}
    </Flex>
  );
};

export default LinkButton;
